import axios from "axios";
import { useFormik } from "formik";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as yup from "yup";
import { GlobalContext } from "../../../context/GlobalContext";
import useTranslation from "../../../hooks/translations";
import Forms from "./Forms";
import PaymentSusccessModal from "../../../components/Modals/PaymentSuccessModal";
import {
  CANCELED,
  CONFIRMED,
  EXPIRED,
  FAILED,
  IN_PROGRESS,
  PROCESSING,
  SUCCESS,
} from "../../../constants/transactionStatus";
import PaymentFailedModal from "../../../components/Modals/PaymentFailModal";
import PaymentProcessingModal from "../../../components/Modals/PaymentProcessingModal";
import {
  CREDIT_CARD,
  CYBERSOURCE,
  MOBILE_MONEY,
  PAYPAL,
} from "../../../constants/data";
import { Snackbar, SnackbarContent } from "@mui/material";
import palette from "../../../styles/palette";
import { Backdrop } from "../../../components";
import { appConfig } from "../../../config";
import { getModalByStatus } from "../../../utils/getModalByState";
import { extractPaymentRegexMap } from "../../../utils/extractPaymentRegexMap";
import {
  getProviderServiceCharge,
  getProviderTotalAmount,
} from "../../../utils/paymentCharges";
interface Merchant {
  id: string;
  // add other properties of Merchant if needed
}

const FormDetails = ({
  payment,
  order,
  merchant,
}: {
  payment: any;
  order: any;
  merchant: Merchant;
}) => {
  const {
    setErrorRequest,
    setPaymentProviderName,
    paymentProviderName,
    theme,
    searchResults,
    cardPaymentStatusModalState,
    errorRequest,
    setCardPaymentStatusModalState,
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const [initFormData, setInitFormData] = useState<any>({});
  const [checked, setChecked] = useState(true);
  const [pricingId, setPricingId] = useState({});
  const [paymentProviderId, setPaymentProviderId] = useState({ uuid: "" });
  const [radioChecked, setRadioChecked] = useState(false);
  const [providerType, setProviderType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalState, setModalState] = useState<
    keyof typeof MODAL_COMPONENTS | ""
  >("");
  const [backdropOpen, setBackDropOpen] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [startPollingFlag, setStartPollingFlag] = useState(false);
  const mobileMoney = useMemo(() => {
    if (!Array.isArray(payment)) return null;
    return payment.find((item) => item.name === "Mobile Money") || null;
  }, [payment]);

  const paymentRegexMap = useMemo(() => {
    return mobileMoney ? extractPaymentRegexMap(mobileMoney) : {};
  }, [mobileMoney]);

  const baseNotificationSchema = yup.object({
    mobileNumber: yup
      .string()
      .required(t("paymentPhoneError"))
      .matches(/^.*$/, t("wrongMobileNumber")),
  });

  let notificationSchema = baseNotificationSchema;

  Object.entries(paymentRegexMap).forEach(([uuid, regex]) => {
    const cleanRegex = regex.replace(/^\/|\/$/g, "");
    notificationSchema = notificationSchema.when("paymentProviderId.uuid", {
      is: uuid,
      then: () =>
        baseNotificationSchema.shape({
          mobileNumber: yup
            .string()
            .required(t("paymentPhoneError"))
            .matches(new RegExp(cleanRegex), t("wrongMobileNumber")),
        }),
    });
  });

  const FORM_VALIDATION = yup.object({
    notification: notificationSchema.when("providerType", {
      is: (val: string) => val === CREDIT_CARD,
      then: () =>
        yup.object({
          // customerName: yup
          //   .string()
          //   .max(20, t("maxNumberOfCharacters"))
          //   .required(t("paymentNameError")),
          // mobileNumber: yup
          //   .string()
          //   .required(t("paymentPhoneError"))
          //   .matches(
          //     /^(237|00237|\+237)?(6[0-9]{8})$/,
          //     t("wrongPhoneNumber")
          //   ),
          // email: yup
          //   .string()
          //   .email(t("invalidEmailError"))
          //   .max(50, t("maxNumberOfCharacters"))
          //   .required(t("emailRequiredError")),
        }),
    }),
    providerType: yup.string().required(t("paymentMethodError")),

    // Make paymentProviderId required only when providerType is Mobile Money
    paymentProviderId: yup.object().when("providerType", {
      is: "Mobile Money",
      then: () =>
        yup.object({
          uuid: yup.string().required(t("paymentMethodError")),
        }),
      otherwise: () => yup.object(),
    }),

    // Make walletId required only for safimoney
    walletId: yup
      .string()
      .nullable()
      .when([], {
        is: () => paymentProviderName === "safimoney",
        then: () =>
          yup
            .string()
            .min(16, t("walletIdLength"))
            .max(16, t("walletIdLength"))
            .required(t("walletIdError")),
        otherwise: () => yup.string().nullable(),
      }),
  });

  useEffect(() => {
    let INITIAL_FORM_STATE = {
      id: order?.id ? order?.id : "",
      notification: {
        customerName: order?.customerName ? order?.customerName : "",
        email: order?.email ? order?.email : "",
        mobileNumber: order?.phoneNumber
          ? order?.phoneNumber.length <= 9
            ? `+237${order?.phoneNumber}`
            : order?.phoneNumber
          : "+237",
      },
      providerType: providerType ? providerType : null,
      paymentProviderId: paymentProviderId ? paymentProviderId : { uuid: "" },
      pricingId: pricingId ? pricingId : "",
      totalAmount: order?.amountPaid || null,
      merchantId: merchant?.id ? merchant?.id : null,
      otp: null,
      walletId: null,
    };
    setInitFormData(INITIAL_FORM_STATE);
  }, [
    providerType,
    payment,
    order,
    pricingId,
    paymentProviderId,
    merchant?.id,
  ]);

  const handleChecked = () => {
    setChecked(!checked);
  };
  const getPaypalUrl = (values: any) =>
    `/payment/api/order/paypal/${searchResults?.orderPlacement?.id?.uuid}/init/${values?.paymentProviderId?.uuid}`;
  const getFlowcashUrl = (values: any) =>
    `/payment/api/order/flocash/${searchResults?.orderPlacement?.id?.uuid}/init/${values?.paymentProviderId?.uuid}`;
  const getMobileUrl = (values: any) =>
    `/payment/api/order/${values?.id?.uuid}/payWith/${values?.paymentProviderId?.uuid}`;
  const getCyberSourceUrl = (values: any) =>
    `/payment/api/order/cybersource/${searchResults?.orderPlacement?.id?.uuid}/init/${values?.paymentProviderId?.uuid}`;
  const statusUrl = (id: any) => `/payment/api/order/status?txid=${id}`;

  const isPaypal = providerType === PAYPAL;
  const isCard = providerType === CREDIT_CARD;
  const isMobileMoney = providerType === MOBILE_MONEY;

  const errorOperation: any = useMemo(() => {
    return {
      [MOBILE_MONEY]: () => {
        setModalOpen(true);
        setModalState(FAILED);
        setStartPollingFlag(false);
      },
      [PAYPAL]: () => {
        setBackDropOpen(false);
        setPaymentError(true);
      },
      [CREDIT_CARD]: () => {
        setBackDropOpen(false);
        setPaymentError(true);
      },
      [CYBERSOURCE]: () => {
        setModalOpen(true);
        setModalState(FAILED);
      },
    };
  }, []);

  const processingOperation: any = {
    [MOBILE_MONEY]: () => {
      setModalOpen(true);
      setModalState(PROCESSING);
    },
    [PAYPAL]: () => {
      setBackDropOpen(true);
    },
    [CREDIT_CARD]: () => {
      setBackDropOpen(true);
    },
    [CYBERSOURCE]: () => {
      setModalOpen(true);
      setModalState(PROCESSING);
    },
  };

  const handleCommonError = useCallback(() => {
    errorOperation[providerType]();
  }, [errorOperation, providerType]);

  const statusHandlers: any = useMemo(() => {
    return {
      [CONFIRMED]: () => {
        setModalState(SUCCESS);
        setStartPollingFlag(false);
      },
      [SUCCESS]: () => {
        setModalState(SUCCESS);
      },
      [FAILED]: handleCommonError,
      [EXPIRED]: handleCommonError,
      [CANCELED]: handleCommonError,
      [IN_PROGRESS]: () => null,
    };
  }, [handleCommonError]);

  const handleFormSubmit = (values: any) => {
    delete values["totalAmount"];
    axios.defaults.withCredentials = true;

    processingOperation[providerType]();
    axios({
      method: isPaypal ? "GET" : "PUT",
      baseURL: appConfig.REACT_APP_PROXY_BASE_URL,
      url: isPaypal
        ? getPaypalUrl(values)
        : isCard
          ? getFlowcashUrl(values)
          : getMobileUrl(values),
      withCredentials: true,
      data: values,
    })
      .then((resp) => {
        // if payment method is different from paypal or credit card, use normal flow
        if (resp.status === 200 && !isPaypal && !isCard) {
          setModalOpen(true);
          setStartPollingFlag(true);
          return;
        }
        setBackDropOpen(false);

        window.location.replace(resp.data);
      })
      .catch((error) => {
        let resp = error?.response?.data;
        setErrorRequest({
          code: resp?.errorCodeNumeric,
          message: resp?.message,
        });
        errorOperation[providerType]();
      });
  };

  useEffect(() => {
    let intervalId: any;

    const startPolling = () => {
      intervalId = setInterval(async () => {
        axios({
          method: "GET",
          baseURL: appConfig.REACT_APP_PROXY_BASE_URL,
          url: statusUrl(searchResults?.orderPlacement?.id?.uuid),
          withCredentials: true,
        })
          .then((resp) => {
            if (resp.data.status !== IN_PROGRESS) clearInterval(intervalId);
            if (resp.status === 200) {
              const handler =
                statusHandlers[resp.data.status] || handleCommonError;
              handler();
            } else {
              handleCommonError();
            }
          })
          .catch((error) => {
            errorOperation[providerType]();
          });
      }, 15000);
    };

    if (startPollingFlag) startPolling();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    searchResults,
    startPollingFlag,
    setStartPollingFlag,
    providerType,
    errorOperation,
    statusHandlers,
    handleCommonError,
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { ...initFormData },
    validateOnMount: true,
    validationSchema: FORM_VALIDATION,
    onSubmit: handleFormSubmit,
  });

  const handleCyberSourcePay = useCallback(
    (transientToken: string) => {
      processingOperation[CYBERSOURCE]();
      axios.defaults.withCredentials = true;
      axios({
        method: "PUT",
        baseURL: appConfig.REACT_APP_PROXY_BASE_URL,
        url: getCyberSourceUrl(formik.values),
        withCredentials: true,
        data: {
          notification: formik.values.notification,
          providerType: formik.values.providerType,
          merchantId: formik.values.merchantId?.uuid,
          otp: formik.values.otp,
          cardNumberToken: transientToken,
        },
      })
        .then((resp) => {
          const handler = statusHandlers[resp.data.status] ?? handleCommonError;
          handler();
        })
        .catch((error) => {
          const resp = error?.response?.data;
          setErrorRequest({
            code: resp?.errorCodeNumeric,
            message: resp?.message,
          });
          errorOperation[CYBERSOURCE]();
        });
    },
    [formik.values, errorOperation, searchResults],
  );

  const selectedProviderId = formik.values?.paymentProviderId?.uuid;
  const expandedProvider = Array.isArray(payment)
    ? payment.find((p: any) => p.name === formik.values?.providerType)
    : undefined;
  const summaryServiceCharge = expandedProvider
    ? getProviderServiceCharge(expandedProvider, selectedProviderId)
    : 0;
  const summaryTotal = expandedProvider
    ? getProviderTotalAmount(expandedProvider, selectedProviderId)
    : null;
  const orderAmount = Number(order?.totalAmount) || 0;
  const totalToPay = summaryTotal ?? orderAmount + (summaryServiceCharge || 0);

  const MODAL_COMPONENTS = {
    SUCCESS: PaymentSusccessModal,
    FAILED: PaymentFailedModal,
    PROCESSING: PaymentProcessingModal,
  };

  const CurrentModal = modalState && MODAL_COMPONENTS[modalState];
  const ReturnModal =
    errorRequest?.code &&
    getModalByStatus(errorRequest?.code, MODAL_COMPONENTS);

  const handleModalClose = (
    event: any,
    reason: "escapeKeyDown" | "backdropClick",
  ) => {
    if (reason !== "backdropClick") {
      setModalOpen(false);
    }
  };

  const handleGlobalModalClose = (
    event: any,
    reason: "escapeKeyDown" | "backdropClick",
  ) => {
    if (reason !== "backdropClick") {
      setCardPaymentStatusModalState(false);
    }
  };

  return (
    <>
      {CurrentModal && (
        <CurrentModal
          amount={totalToPay}
          transactionId={searchResults?.orderPlacement?.id?.uuid}
          isOpen={modalOpen}
          onClose={handleModalClose}
          returnUrl={searchResults?.fullReturnUrl}
          providerId={formik.values?.paymentProviderId?.uuid}
        />
      )}
      {ReturnModal && (
        <ReturnModal
          amount={totalToPay}
          transactionId={searchResults?.orderPlacement?.id?.uuid}
          isOpen={cardPaymentStatusModalState}
          onClose={handleGlobalModalClose}
          returnUrl={searchResults?.fullReturnUrl}
          providerId={formik.values?.paymentProviderId?.uuid}
        />
      )}
      <Backdrop open={backdropOpen} />
      <Snackbar
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={paymentError}
        onClose={() => setPaymentError(false)}
      >
        <SnackbarContent
          style={{
            backgroundColor: palette.red,
            fontSize: ".8rem",
          }}
          message={providerType + " " + t("serviceTemporarilyUnavailable")}
        />
      </Snackbar>
      <Forms
        theme={theme}
        formik={formik}
        payment={payment}
        setPaymentProviderId={setPaymentProviderId}
        setPricingId={setPricingId}
        setPaymentProviderName={setPaymentProviderName}
        radioChecked={radioChecked}
        setRadioChecked={setRadioChecked}
        paymentProviderName={paymentProviderName}
        checked={checked}
        handleChecked={handleChecked}
        providerType={providerType}
        setProviderType={setProviderType}
        onCyberSourcePay={handleCyberSourcePay}
        amount={formik.values.totalAmount}
        orderTotalAmount={order?.totalAmount}
        currency={order?.currency}
      />
    </>
  );
};
export default FormDetails;
